import {
  doc,
  runTransaction,
  serverTimestamp,
  type DocumentData,
  type Firestore,
} from 'firebase/firestore';
import { legacyMessageOwnsPreview, RECALLED_THREAD_PREVIEW_TEXT } from './messagePreview.ts';

/**
 * D6 (docs/audits/refute-2026-09-16/AUDIT_D_messages_boards.md): recall
 * used to read the thread, decide the preview, then write a plain
 * `writeBatch` with no precondition. A reply that landed between the read
 * and the commit had its own preview overwritten with "Message recalled",
 * because nothing re-checked `lastMessageId` at commit time.
 *
 * The whole thing now happens inside `runTransaction`, with the read and
 * the decision INSIDE the callback, on the same snapshot the write commits
 * against - but that alone does not fix the race. Proven against the real
 * Firestore rules emulator (tests/message-recall-race.test.mjs): the
 * deployed rule (`isValidLastMessagePreviewUpdate` in
 * shared/firestore.rules) already ties authorization for a preview write
 * to the thread's LIVE `lastMessageId`, not to whatever this transaction
 * read, so a reply landing in the gap is never silently overwritten - the
 * rule itself refuses the stale write. But that refusal comes back as
 * `permission-denied`, and Firestore's client SDK only auto-retries a
 * transaction on `aborted`, `failed-precondition` or `already-exists`
 * (`isRetryableTransactionError` in the SDK) - `permission-denied` is
 * treated as terminal, so the FIRST attempt's whole transaction, including
 * the harmless message-only recall, was thrown away with no retry at all.
 * Proven by execution: a raced call with no manual retry here fails
 * outright even though the reply's own preview was never touched.
 *
 * So the fix is ONE manual retry, right here, on whatever the first
 * attempt throws. The retry is a fresh `runTransaction` call: it re-reads
 * the thread from scratch, this time sees the reply's own `lastMessageId`,
 * correctly decides this message no longer owns the preview, and commits
 * a message-only recall with no thread write at all - the exact shape
 * already proven to succeed for a non-current message
 * (tests/firestore-rules.test.mjs, "Isolate the message-write boundary
 * from preview authorization"). A second failure (a real authorization
 * problem, not a race) surfaces normally; only one retry is attempted,
 * matching the audit's own description of the window as "one round trip".
 */
export interface RecallMessageOptions {
  /**
   * Test-only seam. Invoked once per transaction attempt, after the reads
   * and before any write, so a test can land a concurrent write in that
   * exact gap and prove the retry re-reads fresh data rather than trusting
   * its first read. Never set outside a test; every real call omits it and
   * it is then a no-op.
   */
  onReadyToCommitForTest?: () => Promise<void> | void;
}

export async function recallMessage(
  firestore: Firestore,
  threadId: string,
  messageId: string,
  originalText?: string,
  options?: RecallMessageOptions
): Promise<void> {
  const messageRef = doc(firestore, 'threads', threadId, 'messages', messageId);
  const threadRef = doc(firestore, 'threads', threadId);

  const attempt = (): Promise<void> => runTransaction(firestore, async (tx) => {
    let previewPatch: { lastMessageText: string; lastMessageId?: string } | null = null;
    if (originalText !== undefined) {
      try {
        // Both reads must happen before any write in this transaction, and
        // both must happen INSIDE it (not before runTransaction is called)
        // so a fresh attempt (the manual retry below, or an automatic SDK
        // retry on real contention) re-reads this same current data.
        const [threadSnap, messageSnap] = await Promise.all([
          tx.get(threadRef),
          tx.get(messageRef),
        ]);
        if (threadSnap.exists()) {
          const data = threadSnap.data() as DocumentData;
          if (data.lastMessageId === messageId) {
            previewPatch = { lastMessageText: RECALLED_THREAD_PREVIEW_TEXT };
          } else if (
            messageSnap.exists()
            && legacyMessageOwnsPreview(data, messageSnap.data() as DocumentData)
          ) {
            previewPatch = { lastMessageText: RECALLED_THREAD_PREVIEW_TEXT, lastMessageId: messageId };
          }
        }
      } catch {
        /* preview stays stale - non-critical, the recall itself still commits */
      }
    }
    await options?.onReadyToCommitForTest?.();
    tx.update(messageRef, {
      recalled: true,
      recalledAt: serverTimestamp(),
      text: '',
      mentions: [],
      attachments: [],
    });
    if (previewPatch) tx.update(threadRef, previewPatch);
  });

  try {
    await attempt();
  } catch {
    // See the module docblock: a raced reply surfaces as permission-denied,
    // which the SDK does not retry on its own. One fresh attempt re-reads
    // the thread and recomputes the decision against current data.
    await attempt();
  }
}
